import { CloseOutlined, DeleteOutlined, HistoryOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Card, Empty, Flex, Popconfirm, Skeleton, Spin, Typography, message } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useChat } from "src/contexts/chat.context";
import { dictionary, i18n } from "src/utils";
import AiAgentApi from "./api";
import { AiAgentChatType, ChatOption } from "./enums";
import "./spin.css";
import { contentStyle, fullWidth } from "./styles";
import AiAgentTitleTag from "./title-tag";
import { type AiAgentHistory } from "./types";

const { Text } = Typography;

interface HistoryProps {
  token: string;
  isAdmin: boolean;
}

function AiConversationTitleTag(props: { type: AiAgentChatType }) {
  return (
    <div
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 8,
        background: props.type === AiAgentChatType.LiveChat ? "#F0F5FF" : "#CFACE1",
        color: props.type === AiAgentChatType.LiveChat ? "#2F54EB" : "#884EA6",
        border: `1px solid ${props.type === AiAgentChatType.LiveChat ? "#ADC6FF" : "#AF50B4"}`,
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "12px",
        lineHeight: "20px",
        letterSpacing: "0",
      }}
    >
      {props.type === AiAgentChatType.LiveChat ? i18n("aiAgent.liveChat") : i18n("aiAgent.aiAgent")}
    </div>
  );
}

export default function History({ token, isAdmin }: HistoryProps) {
  const { option, setOption } = useChat();
  const [messageApi, contextHolder] = message.useMessage();

  const [data, setData] = useState<AiAgentHistory[]>([]);
  const [loading, setLoading] = useState(false);

  async function getData() {
    if (option !== ChatOption.HISTORY) return;
    setLoading(true);
    const tempData = await new AiAgentApi(token).getHistory();
    setData(tempData);
    setLoading(false);
  }

  useEffect(() => {
    getData();
  }, [option]);

  function handleSelectConversation(conversation: AiAgentHistory) {
    const finalConversation = {
      id: conversation.chat.id,
      createdAt: dayjs().toISOString(),
      messages: conversation.messages.map(({ id, role, content, feedback, processedAt, avatarUrl }) => ({
        id,
        role,
        content,
        feedback,
        processedAt,
        avatarUrl,
      })),
      usage: conversation.usage,
      type: conversation.chat.type,
    };
    localStorage.setItem("current-conversation", JSON.stringify(finalConversation));
    localStorage.setItem("ai_chat_option", ChatOption.CHAT.toString());
    setOption(ChatOption.CHAT);
  }

  return (
    <>
      {contextHolder}
      <Flex
        style={{
          ...contentStyle,
          width: "100%",
          height: "100%",
          display: option !== ChatOption.HISTORY ? "none" : undefined,
        }}
        vertical
        gap={16}
      >
        <Flex
          style={{ ...fullWidth }}
          justify="space-between"
          align="center"
          gap={8}
        >
          <AiAgentTitleTag name={i18n(dictionary.label.history)} />
          <Flex gap={8}>
            <Button
              size="small"
              shape="round"
              icon={<PlusOutlined />}
              onClick={() => {
                setData([]);
                localStorage.removeItem("current-conversation");
                localStorage.setItem("ai_chat_option", ChatOption.CHAT.toString());
                setOption(ChatOption.CHAT);
              }}
            >
              {i18n("aiAgent.newConversation.button")}
            </Button>
            <Button
              size="small"
              shape="circle"
              icon={<ReloadOutlined />}
              onClick={getData}
            />
            <Button
              shape="circle"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => {
                setData([]);
                localStorage.setItem("ai_chat_option", ChatOption.NONE.toString());
                setOption(ChatOption.NONE);
              }}
            />
          </Flex>
        </Flex>
        {loading && !data?.length ? (
          <Skeleton
            style={{
              ...fullWidth,
              height: "100%",
              overflowY: "auto",
            }}
          />
        ) : data && data.length ? (
          <Spin
            wrapperClassName="full-width-spin"
            spinning={loading && data && !!data.length}
          >
            <Flex
              style={{
                ...fullWidth,
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
                paddingRight: 8,
              }}
              vertical
              gap={16}
            >
              {data.map((element) => (
                <Card
                  key={element.chat.id}
                  style={{
                    borderRadius: 16,
                    borderColor: "#CFACE1",
                    boxShadow: "0 3px 5px #0000005c",
                  }}
                  styles={{ body: { padding: "8px 12px" } }}
                >
                  <AiConversationTitleTag type={element.chat.type} />
                  <Flex
                    justify="space-between"
                    align="center"
                  >
                    <Flex
                      vertical
                      gap={4}
                    >
                      <Flex gap={4}>
                        <Text
                          style={{
                            maxWidth: 256,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontWeight: 600,
                          }}
                        >
                          {element.chat.title}
                        </Text>
                      </Flex>
                      <Text type="secondary">{dayjs(element.chat.createdAt).format(i18n("fullDateFormat"))}</Text>
                    </Flex>
                    <Flex>
                      <Button
                        type="text"
                        icon={<HistoryOutlined style={{ color: "#884EA6" }} />}
                        onClick={() => {
                          handleSelectConversation(element);
                        }}
                      />
                      <Popconfirm
                        title={i18n("aiAgent.history.deleteConversationConfirmation")}
                        okText={i18n("yes")}
                        cancelText={i18n("no")}
                        onConfirm={async () => {
                          const response = await new AiAgentApi(token).inactivateConversation(element.chat.id);
                          if (response) {
                            messageApi.success(i18n("aiAgent.history.deleteConversationNotification.success"));
                            getData();
                            localStorage.removeItem("current-conversation");
                          } else {
                            messageApi.error(i18n("aiAgent.history.deleteConversationNotification.error"));
                          }
                        }}
                      >
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          disabled={isAdmin}
                        />
                      </Popconfirm>
                    </Flex>
                  </Flex>
                </Card>
              ))}
            </Flex>
          </Spin>
        ) : (
          <Empty />
        )}
      </Flex>
    </>
  );
}
